import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ArrowLeft, LogIn, LogOut, Clock, History, Lock, Ticket as TicketIcon, Camera, Loader2, Gift, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { StaminaBadge } from "@/components/StaminaBadge";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (u ? `${BACKEND}${u}` : null);

const TX_LABEL = {
  PRESENCE_REWARD: "Permanenza", FREQUENCY_REWARD: "Bonus frequenza", SURVEY_REWARD: "Questionario",
  BIRTHDAY_GIFT: "Regalo compleanno", RAFFLE_REWARD: "Estrazione",
  MANUAL_ADJUSTMENT: "Rettifica manuale", REDEEM: "Riscatto", REVERSAL: "Storno", LEGACY_EARN: "Storico (legacy)",
};

function liveDuration(iso) {
  const m = Math.floor(Math.max(Date.now() - new Date(iso).getTime(), 0) / 60000);
  const h = Math.floor(m / 60);
  return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export default function PlusCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tk, setTk] = useState({ loaded_amount: "", wagered_amount: "", number_of_games: "" });
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const [, setTick] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeeming, setRedeeming] = useState(null);

  const load = useCallback(async () => {
    const [c, t, s, ta, rw] = await Promise.all([
      api.get(`/plus/customers/${id}`),
      api.get(`/plus/customers/${id}/transactions`),
      api.get(`/plus/customers/${id}/sessions`),
      api.get(`/plus/customers/${id}/ticket-analytics`),
      api.get(`/plus/rewards`),
    ]);
    setData(c.data); setTxs(t.data); setSessions(s.data); setTickets(ta.data); setRewards(rw.data);
  }, [id]);

  useEffect(() => { load().catch(() => navigate("/")); }, [load, navigate]);
  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 15000);
    return () => clearInterval(i);
  }, []);

  const checkIn = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/plus/sessions/checkin", { customer_id: id });
      toast.success("Check-in registrato");
      if (res.birthday_gift) toast.success(`🎂 Regalo di compleanno · +${res.birthday_gift.points_change} ⭐`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const checkOut = async () => {
    if (!data?.active_session) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/plus/sessions/${data.active_session.id}/checkout`, {});
      if (res.stars_earned > 0) toast.success(`Check-out · +${res.stars_earned} ⭐ (permanenza ${Math.round(res.duration_minutes)} min)`);
      else toast.info(`Check-out · visita ${Math.round(res.duration_minutes)} min, nessuna Stella (permanenza minima non raggiunta)`);
      if (res.frequency_stars > 0) toast.success(`🔥 Bonus frequenza · +${res.frequency_stars} ⭐`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const addTicket = async (e) => {
    e.preventDefault();
    const payload = { customer_id: id };
    ["loaded_amount", "wagered_amount", "number_of_games"].forEach((k) => { if (tk[k] !== "") payload[k] = Number(tk[k]); });
    if (payload.loaded_amount == null && payload.wagered_amount == null && payload.number_of_games == null) {
      toast.error("Inserisci almeno un valore"); return;
    }
    setBusy(true);
    try {
      await api.post("/plus/ticket-analytics", payload);
      toast.success("Dato ticket archiviato (nessuna Stella generata)");
      setTk({ loaded_amount: "", wagered_amount: "", number_of_games: "" });
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const scanTicket = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("customer_id", id);
      const { data: res } = await api.post("/plus/ticket-analytics/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = res.extracted || {};
      toast.success(`Ticket scansionato · Puntato ${ex.importo ?? "–"}€ · Stamina aggiornata (nessuna Stella)`);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "DUPLICATE_TICKET") toast.error("Ticket già registrato (anti-frode)");
      else toast.error(apiError(err));
    } finally { setScanning(false); }
  };

  const redeem = async (reward) => {
    setRedeeming(reward.id);
    try {
      const { data: res } = await api.post("/plus/points/redeem", {
        customer_id: id, reward_id: reward.id,
        idempotency_key: `${id}-${reward.id}-${Date.now()}`,
      });
      toast.success(`Premio riscattato · ${res.reward_nome} · -${res.points_used} ⭐ (saldo ${res.balance_after})`);
      setShowRedeem(false);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setRedeeming(null); }
  };

  if (!data) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">...</div>;
  const c = data.customer;
  const session = data.active_session;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button data-testid="plus-customer-back" onClick={() => navigate("/")}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" /> Home
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">{c.nome} {c.cognome}</h1>
            <p className="text-zinc-500">{c.cellulare}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm ${session ? "bg-green-500/15 text-green-400 border border-green-500/30" : "bg-zinc-800 text-zinc-400 border border-zinc-700"}`}>
            {session ? "🟢 Presente in sala" : "Non presente"}
          </span>
        </div>

        {/* SEZIONE CLIENTE / FIDELITY */}
        <div data-testid="plus-fidelity-section" className="rounded-3xl bg-gradient-to-br from-amber-500/10 to-zinc-900 border border-amber-400/30 p-6 mb-5">
          <div className="text-xs uppercase tracking-widest text-amber-400/70 mb-2">Sezione Cliente · Fidelity</div>
          <div className="flex items-center gap-3">
            <Star className="w-10 h-10 text-amber-400 fill-amber-400" />
            <div>
              <div data-testid="plus-stella-balance" className="font-display text-4xl font-bold text-amber-400">{data.balance}</div>
              <div className="text-zinc-400 text-sm">Punti Stella disponibili</div>
            </div>
            <button data-testid="plus-redeem-open" onClick={() => setShowRedeem(true)}
              className="ml-auto flex items-center gap-2 px-5 h-12 rounded-2xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold transition-colors">
              <Gift className="w-5 h-5" /> Riscatta premio
            </button>
          </div>
        </div>

        {/* SESSIONE / PRESENZA */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Visita / Presenza</div>
              {session ? (
                <div className="flex items-center gap-2">
                  <Clock className="w-6 h-6 text-green-400" />
                  <span data-testid="plus-session-timer" className="font-display text-3xl font-bold text-green-400">{liveDuration(session.check_in)}</span>
                  {session.zone_current && <span className="text-xs text-zinc-400 px-2 py-0.5 rounded-full bg-zinc-800">{session.zone_current}</span>}
                </div>
              ) : (
                <span className="text-zinc-500">Nessuna visita in corso</span>
              )}
            </div>
            {session ? (
              <button data-testid="plus-checkout-btn" onClick={checkOut} disabled={busy}
                className="flex items-center gap-2 px-6 h-14 rounded-2xl bg-red-500/90 hover:bg-red-500 font-bold text-lg transition-colors disabled:opacity-60">
                <LogOut className="w-5 h-5" /> Check-out
              </button>
            ) : (
              <button data-testid="plus-checkin-btn" onClick={checkIn} disabled={busy}
                className="flex items-center gap-2 px-6 h-14 rounded-2xl bg-green-500 hover:bg-green-400 text-zinc-900 font-bold text-lg transition-colors disabled:opacity-60">
                <LogIn className="w-5 h-5" /> Check-in
              </button>
            )}
          </div>
        </div>

        {/* CUSTOMER VALUE — sezione gestionale interna */}
        {(() => {
          const cv = data.customer_value;
          if (!cv || cv.available === false) {
            return (
              <div data-testid="plus-customer-value-section" className="rounded-3xl bg-zinc-900/60 border border-dashed border-zinc-700 p-6 mb-8">
                <div className="flex items-center gap-2 text-zinc-500"><Lock className="w-5 h-5" /><span className="text-xs uppercase tracking-widest">Sezione interna · Customer Value</span></div>
                <p className="text-zinc-500 text-sm mt-3">Nessun dato ancora calcolato per questo cliente.</p>
              </div>
            );
          }
          const color = cv.level_color || "#67e8f9";
          return (
            <div data-testid="plus-customer-value-section" className="rounded-3xl bg-zinc-900 border p-6 mb-8" style={{ borderColor: `${color}55` }}>
              <div className="flex items-center gap-2 mb-3 text-zinc-400"><Lock className="w-4 h-4" /><span className="text-xs uppercase tracking-widest">Sezione interna · Customer Value (gestionale)</span></div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <StaminaBadge level={cv.customer_level} color={color} size={36} showLabel={false} />
                  <div data-testid="plus-cv-level" className="font-display text-2xl font-bold mt-1" style={{ color }}>{cv.customer_level}</div>
                  {cv.can_view_details && <div className="text-zinc-500 text-xs mt-1">Classe</div>}
                </div>
                {cv.can_view_details && (
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span data-testid="plus-cv-score" className="font-display text-3xl font-bold" style={{ color }}>{cv.score}<span className="text-zinc-600 text-lg">/100</span></span>
                      {cv.trend && <span className={`text-sm font-semibold ${cv.trend === "CRESCENTE" ? "text-green-400" : cv.trend === "DECRESCENTE" ? "text-red-400" : "text-zinc-400"}`}>{cv.trend === "CRESCENTE" ? "↑" : cv.trend === "DECRESCENTE" ? "↓" : "→"} {cv.trend}</span>}
                    </div>
                    <div className="h-3 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${cv.score}%`, background: color }} />
                    </div>
                  </div>
                )}
              </div>
              {cv.can_view_details && cv.components && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {Object.entries(cv.components).map(([k, v]) => (
                    <div key={k} className="rounded-xl bg-zinc-800/50 border border-zinc-700 px-3 py-2">
                      <div className="text-[10px] uppercase text-zinc-500">{k}</div>
                      <div className="font-semibold text-zinc-200">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-zinc-600 text-xs mt-3">Indice gestionale interno. NON influisce sui Punti Stella, sui premi o sul catalogo.</p>
            </div>
          );
        })()}

        {/* ANALITICA TICKET — solo Customer Value, mai Stelle */}
        <div data-testid="plus-ticket-analytics-section" className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-5">
          <div className="flex items-center gap-2 mb-1"><TicketIcon className="w-5 h-5 text-sky-400" /><h2 className="font-display font-bold text-lg">Analitica Ticket</h2>
            <button data-testid="plus-scan-ticket-btn" onClick={() => fileRef.current?.click()} disabled={scanning}
              className="ml-auto flex items-center gap-2 px-4 h-10 rounded-xl bg-sky-500 hover:bg-sky-400 font-semibold text-sm transition-colors disabled:opacity-60">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Scansiona ticket VLT
            </button>
            <input ref={fileRef} data-testid="plus-scan-file" type="file" accept="image/*" capture="environment" onChange={scanTicket} className="hidden" />
          </div>
          <p className="text-zinc-500 text-xs mb-4">Dati di gioco archiviati per il Customer Value. <b className="text-zinc-400">Non generano Punti Stella.</b> Scansiona il ticket oppure inserisci a mano.</p>
          <form onSubmit={addTicket} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <input data-testid="plus-tk-loaded" type="number" step="0.01" placeholder="Caricato €" value={tk.loaded_amount} onChange={(e) => setTk((s) => ({ ...s, loaded_amount: e.target.value }))} className="h-11 px-3 rounded-xl bg-zinc-800 border border-zinc-700 text-sm focus:outline-none focus:border-sky-400" />
            <input data-testid="plus-tk-wagered" type="number" step="0.01" placeholder="Puntato €" value={tk.wagered_amount} onChange={(e) => setTk((s) => ({ ...s, wagered_amount: e.target.value }))} className="h-11 px-3 rounded-xl bg-zinc-800 border border-zinc-700 text-sm focus:outline-none focus:border-sky-400" />
            <input data-testid="plus-tk-games" type="number" placeholder="N° partite" value={tk.number_of_games} onChange={(e) => setTk((s) => ({ ...s, number_of_games: e.target.value }))} className="h-11 px-3 rounded-xl bg-zinc-800 border border-zinc-700 text-sm focus:outline-none focus:border-sky-400" />
            <button data-testid="plus-tk-submit" type="submit" disabled={busy} className="h-11 rounded-xl bg-sky-500/90 hover:bg-sky-500 font-semibold text-sm transition-colors disabled:opacity-60">Registra</button>
          </form>
          {tickets.length > 0 && (
            <div className="space-y-1">
              {tickets.slice(0, 8).map((t) => (
                <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0 text-sm text-zinc-400">
                  <span>{new Date(t.created_at).toLocaleString("it-IT")}</span>
                  <span>Car. {t.loaded_amount ?? "–"}€ · Punt. {t.wagered_amount ?? "–"}€ · {t.number_of_games ?? "–"} part.</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STORICO STELLE */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-5">
          <div className="flex items-center gap-2 mb-4"><History className="w-5 h-5 text-amber-400" /><h2 className="font-display font-bold text-lg">Storico Stelle</h2></div>
          {txs.length === 0 ? <p className="text-zinc-500 text-sm">Nessun movimento.</p> : (
            <div className="space-y-2">
              {txs.slice(0, 20).map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0 text-sm">
                  <div>
                    <span className="font-medium">{TX_LABEL[t.transaction_type] || t.transaction_type}</span>
                    {t.note && <span className="text-zinc-500"> · {t.note}</span>}
                    <div className="text-zinc-600 text-xs">{new Date(t.created_at).toLocaleString("it-IT")}</div>
                  </div>
                  <span className={`font-bold ${t.points_change >= 0 ? "text-green-400" : "text-red-400"}`}>
                    {t.points_change >= 0 ? "+" : ""}{t.points_change} ⭐
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STORICO VISITE */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6">
          <div className="flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-green-400" /><h2 className="font-display font-bold text-lg">Storico visite</h2></div>
          {sessions.length === 0 ? <p className="text-zinc-500 text-sm">Nessuna visita.</p> : (
            <div className="space-y-2">
              {sessions.slice(0, 15).map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0 text-sm">
                  <div>
                    <span className="text-zinc-300">{new Date(s.check_in).toLocaleString("it-IT")}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${s.stato === "OPEN" ? "bg-green-500/15 text-green-400" : s.stato === "INVALID" ? "bg-zinc-700 text-zinc-400" : "bg-zinc-800 text-zinc-400"}`}>{s.stato}</span>
                  </div>
                  <div className="text-right">
                    {s.duration_minutes != null && <span className="text-zinc-400">{Math.round(s.duration_minutes)} min</span>}
                    {s.stars_awarded > 0 && <span className="ml-2 text-amber-400 font-semibold">+{s.stars_awarded} ⭐</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showRedeem && (
          <div data-testid="plus-redeem-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowRedeem(false)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 p-5 border-b border-zinc-800">
                <Gift className="w-5 h-5 text-amber-400" />
                <h2 className="font-display font-bold text-lg">Riscatta premio</h2>
                <span className="ml-2 inline-flex items-center gap-1 text-amber-400 text-sm font-semibold"><Star className="w-4 h-4 fill-current" /> {data.balance} disponibili</span>
                <button data-testid="plus-redeem-close" onClick={() => setShowRedeem(false)} className="ml-auto w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-zinc-500"><X className="w-4 h-4 text-zinc-400" /></button>
              </div>
              <div className="p-5 overflow-y-auto">
                {rewards.length === 0 ? (
                  <p className="text-zinc-500 text-sm">Nessun premio disponibile. Aggiungili dal pannello Admin.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {rewards.map((r) => {
                      const soldOut = r.stock_tracking && r.stock_quantity <= 0;
                      const tooExpensive = data.balance < r.points_required;
                      const disabled = soldOut || tooExpensive || redeeming === r.id;
                      return (
                        <div key={r.id} data-testid={`plus-redeem-item-${r.id}`} className="rounded-2xl bg-zinc-800/50 border border-zinc-700 overflow-hidden flex flex-col">
                          <div className="h-28 bg-zinc-800 flex items-center justify-center overflow-hidden">
                            {r.image_url ? <img src={imgUrl(r.image_url)} alt={r.nome} className="w-full h-full object-cover" /> : <Gift className="w-8 h-8 text-zinc-600" />}
                          </div>
                          <div className="p-3 flex flex-col flex-1">
                            <div className="font-semibold">{r.nome}</div>
                            {r.descrizione && <div className="text-zinc-500 text-xs mt-0.5 flex-1">{r.descrizione}</div>}
                            <div className="flex items-center justify-between mt-2">
                              <span className="inline-flex items-center gap-1 text-amber-400 font-bold text-sm"><Star className="w-3.5 h-3.5 fill-current" /> {r.points_required}</span>
                              {r.stock_tracking && <span className={`text-[10px] px-2 py-0.5 rounded-full ${soldOut ? "bg-red-500/15 text-red-400" : "bg-zinc-700 text-zinc-400"}`}>{soldOut ? "Esaurito" : `${r.stock_quantity} disp.`}</span>}
                            </div>
                            <button data-testid={`plus-redeem-confirm-${r.id}`} onClick={() => redeem(r)} disabled={disabled}
                              className="mt-3 h-10 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                              {redeeming === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : tooExpensive ? "Punti insufficienti" : soldOut ? "Esaurito" : "Riscatta"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
